/**
 * External apps are applications that run outside of Electron, spawned using Node.js' child_process API.
 * External apps can either be pre-installed (spawned by "path") or downloaded and then spawned (spawned by "alias").
 *
 * Downloaded apps must be contained in a zip file. This file is downloaded via the net module which makes use
 * of Chromium's caching. This exposes some nuances which must be understood: asset entries that are downloaded must
 * contain a version number. A new file is only downloaded if that version number changes. If we were to download
 * the asset with every session then the operating system would think it was a new executable every time and would
 * harass the end user with prompts. But, since downloads use the net module, simply re-downloading does not ensure
 * a fresh download since it might come directly out of Chromium's cache. This can confuse administrators who rightly
 * assume that removing the asset will provoke a fresh download directly from the remote server. So to ensure a fresh
 * download, the `no-cache` header is sent with download requests so that Chromium's cache is bypassed.
 *
 * Assets are downloaded into Electron's "AppData" folder (_%appdata%/<desktop name | Electron>/e2o/assets_.) Note: this location can be changed
 * by setting `externalAssets.assetsFolder` in the manifest. Manually removing an asset from this folder will cause FEA to
 * re-download the asset upon restart. Developers may use the `FSBL.System.deleteAssets()` to delete all downloaded assets.
 * A restart is still required to trigger the fresh download. This function is protected by a security profile (enabled
 * by default in the "trusted" profile.)
 */
import { getOrCreateMainLogger } from "../logger/main";
import { Asset } from "../types/types";
import { ChildProcess, spawn, spawnSync, SpawnOptionsWithoutStdio } from "child_process";
import { StandardResponse, StandardPromise } from "../types/types";
import { stream } from "./download";
import unzipper from "unzipper";
import { pathJoinSecureRoot, isFulfilled, tryCatchNotify, objectGetValueCaseInsensitive, retry } from "../util";
import * as fs from "fs";
import path from "path";
import { mkdirp } from "mkdirp";
import internal from "stream";
import fse from "fs-extra";
// Workaround to pass the depcheck. The package is used under the hood in unzipper package: https://github.com/ZJONSSON/node-unzipper/issues/330
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as s3 from "@aws-sdk/client-s3";

const logger = getOrCreateMainLogger();

type ExternalApp = {
	filePath: string;
	kill: (signal?: number | NodeJS.Signals | undefined) => void;
	/** Resolves when the child process has exited. */
	waitForClose: () => Promise<void>;
};

/**
 * Spawns an external app using Node.js child_process.spawn(). This is a synchronous operation and does
 * not detect spawning failures at the operating system level because it is not possible
 * (https://github.com/nodejs/help/issues/1191) as such, any errors that occur after spawning are logged.
 * @param params
 * @param params.path The path of the executable (see Node.js child_process.spawn)
 * @param params.arguments Optional arguments to send to the executable (see Node.js child_process.spawn)
 * @param params.options Spawning options (see Node.js child_process.spawn)
 * @returns An ExternalApp reference which can be used to kill the app
 */
const spawnApp = ({
	filePath,
	arguments: spawnArguments,
	options,
}: {
	filePath: string;
	arguments: string[];
	options?: SpawnOptionsWithoutStdio | undefined;
}): StandardResponse<ExternalApp> => {
	logger.log(`Spawning external application: ${filePath}`);

	let nodeAppReference: ChildProcess | null = null;

	try {
		// The path.join ensures that the path is in the correct form for this operating system
		nodeAppReference = spawn(path.join("", filePath), spawnArguments, options);
	} catch (e: any) {
		const err = `Failed to spawn child process ${filePath} ${e.toString()}`;
		logger.error(err);
		return { err };
	}

	let resolveClose: () => void = () => {};
	const closePromise = new Promise<void>((resolve) => {
		resolveClose = resolve;
	});

	const externalApp: ExternalApp = {
		filePath,
		/**
		 * Sends a signal to the external application process. Defaults to SIGKILL (uncatchable),
		 * which is appropriate for the force-kill path in closeAllAppsAndWait. Pass a different
		 * signal (e.g. SIGTERM) for a graceful shutdown attempt before resorting to force-kill.
		 */
		kill: (signal: number | NodeJS.Signals | undefined = "SIGKILL") => {
			logger.log(`Closing external application ${filePath} with ${signal}`);
			// Check that the process was successfully spawned before attempting to send a signal.  pid is undefined if
			// spawn() failed at the OS level. ESRCH in the catch below handles the case where the process has since
			// exited.
			if (nodeAppReference?.pid) {
				try {
					if (process?.platform === "win32" && signal === "SIGKILL") {
						logger.log(`taskkill external application ${filePath} (pid ${nodeAppReference.pid}).`);
						const taskkillResult = spawnSync("taskkill", ["/PID", `${nodeAppReference.pid}`, "/T", "/F"], {
							windowsHide: true,
							stdio: "ignore",
						});

						if (taskkillResult?.error || (taskkillResult?.status !== 0 && taskkillResult?.status !== 128)) {
							logger.warn(`taskkill failed with error: ${taskkillResult.error}, status: ${taskkillResult.status}`);
							nodeAppReference.kill(signal); // call node process kill as a fallback.
						}
					} else {
						nodeAppReference.kill(signal); // call node process kill as a fallback.
					}
				} catch (err) {
					// ESRCH: process already exited between the pid check and kill(); treat as already closed.
					// EPERM: insufficient permissions; log and continue so shutdown is not blocked.
					logger.warn(`Failed to kill external application ${filePath}: ${err}`);
				}
			}
		},
		waitForClose: () => closePromise,
	};

	nodeAppReference.on("error", (err) => {
		logger.error(`Child process ${filePath} error: ${err.toString()}`);
	});

	nodeAppReference.stderr?.on("data", (stderr) => {
		logger.error(`Child process ${filePath} stderr: ${stderr}`);
	});

	nodeAppReference.stdout?.on("data", (stdout) => {
		logger.debug(`Child process ${filePath} stdout: ${stdout}`);
	});

	nodeAppReference.on("close", (code) => {
		logger.log(`Child process ${filePath} exited with code ${code}`);
		resolveClose();
	});

	return { data: externalApp };
};

type DownloadedAsset = {
	alias: string;
	executable: string;
	version?: string;
	assetFolder: string;
	url: string;
};

/**
 * Ensures that arguments are an array.
 *
 * @param spawnPath Path of the asset
 * @param spawnArguments Arguments in either string or array form
 * @returns An array of strings that can be passed to Node.js child_process.spawn as arguments
 */
const argumentsToArray = (spawnPath: string, spawnArguments: any): string[] => {
	if (typeof spawnArguments === "string") {
		// If arguments is a string, split it into an array
		// https://stackoverflow.com/a/2817670
		const matches = spawnArguments.match(/[^\s"]+|"([^"]*)"/g);

		// match can return null if there are not matches to the regex.
		if (matches === null) {
			logger.warn(`ExternalApplicationManager.spawn() Illegal arguments: ${spawnArguments}`);
			return [spawnArguments];
		} else {
			return matches.map((arg: string) => {
				// child_process.spawn doesn't handle quotes around arguments, so they should be removed.
				if (arg.startsWith('"') && arg.endsWith('"')) {
					arg = arg.substring(1, arg.length - 1);
				}

				return arg;
			});
		}
	} else if (Array.isArray(spawnArguments)) {
		return spawnArguments;
	}
	if (spawnArguments) {
		logger.warn(`Arguments for ${spawnPath} is not a string or an array, but is ${typeof spawnArguments}`);
	}
	return [];
};

/**
 * Downloads and unzips an asset in a Stream pipeline.
 *
 * @param fileStream - A Node.js Stream object (from download.ts)
 * @param downloadAsset - The asset to be downloaded
 * @returns StandardPromise - Void unless error
 */
const downloadAndUnzip = async (
	fileStream: internal.Transform,
	downloadAsset: DownloadedAsset
): StandardPromise<void> =>
	new Promise((resolve) => {
		// eslint-disable-next-line new-cap
		const unzipperStream = unzipper.Extract({ path: downloadAsset.assetFolder }).on("close", () => {
			resolve({});
		});
		const deadManSwitch = setInterval(() => {
			// Since unzip-stream close event is unreliable we add this dead man switch.
			// The unfinishedEntries private member will be zero when extraction is complete
			if ((unzipperStream as any).unfinishedEntries === 0) {
				clearInterval(deadManSwitch);
				resolve({});
			}
		}, 1000);
		fileStream
			.pipe(unzipperStream)
			.on("error", (e: any) => {
				resolve({ err: `Unzip error: ${e.toString()}` });
			})
			.on("close", () => {
				// The original npm unzipper library emitted "close" when an archive was complete
				// unzipper stopped workin with Node 18.16.0
				// https://github.com/ZJONSSON/node-unzipper/issues/271
				resolve({});
			});
		fileStream.on("error", (e) => {
			resolve({ err: `File read error ${e.toString()}` });
		});
	});

/**
 */
export class ExternalApplicationManager {
	assets: Record<string, DownloadedAsset> = {};

	assetsFolder: string;

	externalApps: ExternalApp[] = [];

	constructor(assetsFolder: string) {
		this.assetsFolder = assetsFolder;
		logger.log(`Setting externalAssetsFolder to: ${this.assetsFolder}`);

		if (!fs.existsSync(this.assetsFolder)) {
			mkdirp.sync(this.assetsFolder);
		}
	}

	/**
	 * Fetches cached asset metadata (etag and version) if they for asset with same alias and url
	 * @param etagFilePath metadata file path
	 * @param executablePath
	 * @param currentUrl
	 * @returns object with etag and version metadata
	 */
	getCachedAssetInfo(
		etagFilePath: string,
		executablePath: string,
		currentUrl: string
	): { etag?: string; version?: string } {
		// Determine existence of etag and if we have cached the asset at this url
		if (!fs.existsSync(etagFilePath) || !fs.existsSync(executablePath)) {
			return {};
		}

		const { etag, etagUrl, version } =
			tryCatchNotify(
				() => JSON.parse(fs.readFileSync(etagFilePath, { encoding: "utf8", flag: "r" })),
				"An error occurred while fetching an asset",
				logger
			) ?? {};

		// only return metadata if versions match
		return etagUrl === currentUrl ? { etag, version } : {};
	}

	/**
	 * Downloads and extracts a versioned asset to a version-namespaced subfolder.
	 * Uses a local version file for cache validation — no HTTP request is made on a cache hit.
	 *
	 * Folder layout:
	 *   cache/assets/{alias}-{version}/{alias}/              ← extraction target
	 *   cache/assets/{alias}-{version}/{alias}.version.json  ← metadata: { src, version }
	 */
	private async maybeDownloadVersionedAsset(asset: Asset): StandardPromise<DownloadedAsset> {
		// version is guaranteed non-empty — maybeDownloadAsset only delegates here when asset.version is truthy
		const version = asset.version as string;

		const { err: errV, data: versionedFolder } = pathJoinSecureRoot(
			this.assetsFolder,
			`${asset.alias}-${version}`
		);
		if (errV || !versionedFolder) return { err: errV };

		const { err: errA, data: assetFolder } = pathJoinSecureRoot(versionedFolder, asset.alias);
		if (errA || !assetFolder) return { err: errA };

		const { err: errB, data: versionFilePath } = pathJoinSecureRoot(versionedFolder, `${asset.alias}.version.json`);
		if (errB || !versionFilePath) return { err: errB };

		const { err: errC, data: executablePath } = pathJoinSecureRoot(assetFolder, asset.target);
		if (errC || !executablePath) return { err: errC };

		logger.log(`Checking ${this.assetsFolder} for cached asset "${asset.alias}" v${version}`);

		const downloadAsset = {
			...asset,
			assetFolder,
			executable: asset.target,
			url: asset.src,
		};
		this.assets[asset.alias] = downloadAsset;

		// Cache hit: version file exists with matching src + version and the executable is present
		if (fs.existsSync(versionFilePath) && fs.existsSync(executablePath)) {
			const cached =
				tryCatchNotify(
					() => JSON.parse(fs.readFileSync(versionFilePath, { encoding: "utf8", flag: "r" })),
					"An error occurred while reading versioned asset metadata",
					logger
				) ?? {};
			if (cached.src === asset.src && cached.version === version) {
				logger.log(`Using cached asset "${asset.alias}" v${version} from ${assetFolder}`);
				return { data: downloadAsset };
			} else {
				logger.log(`Cached asset ${cached.src} v${cached.version} is invalid, proceed to download.`);
			}
		}

		// Clear the entire versioned folder (noop if absent) so the download lands cleanly
		try {
			fse.removeSync(versionedFolder);
		} catch (e) {
			return { err: `Failed to remove asset folder ${versionedFolder}: ${e}` };
		}
		fse.ensureDirSync(versionedFolder);
		logger.log(`Downloading asset "${asset.alias}" v${version} from ${asset.src}`);
		const { err, data } = await stream(asset.src, {
			returnStream: true,
			logger,
			headers: { "cache-control": "no-cache" },
		});

		if (err || !data || !data.data) {
			fse.removeSync(assetFolder);
			return {
				err: `Error downloading "${asset.alias}" v${version} from ${asset.src} into ${assetFolder}. Original Error: ${err}`,
			};
		}

		const { err: err2 } = await downloadAndUnzip(data.data, downloadAsset);

		if (err2) {
			fse.removeSync(assetFolder);
			return {
				err: `Error unpacking the file downloaded for "${asset.alias}" v${version} from ${asset.src}.
				Finsemble will attempt to download the file again when the application restarts.
				Original Error: ${err2}`,
			};
		}
		// Write version metadata only after a successful download
		fs.writeFileSync(versionFilePath, JSON.stringify({ src: asset.src, version }));
		logger.log(`Downloaded asset "${asset.alias}" v${version} to ${assetFolder}`);

		this.deleteOldVersionedAssets(asset.alias, asset.maxVersions ?? 3, version);
		return { data: downloadAsset };
	}

	/**
	 * Downloads and extracts an asset if it doesn't yet exist in the e2o cache's assets folder.
	 *
	 * When `asset.version` is set the versioned download path is used (see maybeDownloadVersionedAsset).
	 * Otherwise HTTP ETag-based caching is used.
	 *
	 * @param asset The asset to download
	 */
	// eslint-disable-next-line sonarjs/cognitive-complexity
	async maybeDownloadAsset(asset: Asset): StandardPromise<DownloadedAsset> {
		if (asset.version) {
			return this.maybeDownloadVersionedAsset(asset);
		}

		// Figure out the location to put our asset
		const { err: errA, data: assetFolder } = pathJoinSecureRoot(this.assetsFolder, asset.alias);
		if (errA || !assetFolder) return { err: errA };

		const { err: errB, data: etagFilePath } = pathJoinSecureRoot(this.assetsFolder, `${asset.alias}.etag.json`);
		if (errB || !etagFilePath) return { err: errB };

		const { err: errC, data: executablePath } = pathJoinSecureRoot(assetFolder, asset.target);
		if (errC || !executablePath) return { err: errC };

		logger.info(`Checking ${this.assetsFolder} for cached ${asset.alias}`);

		// Create the folder if it doesn't exist
		if (!fs.existsSync(assetFolder)) {
			mkdirp.sync(assetFolder);
		}

		const downloadAsset = {
			...asset,
			assetFolder: assetFolder,
			executable: asset.target,
			url: asset.src,
		};
		this.assets[asset.alias] = downloadAsset;

		const { etag } = this.getCachedAssetInfo(etagFilePath, executablePath, downloadAsset.url);

		const headers: any = {};

		// If we have an etag, add the If-None-Match header to manually implement caching
		if (etag) {
			headers["If-None-Match"] = etag;
			logger.info(
				`Found existing asset "${downloadAsset.alias}" with etag ${etag} in directory ${downloadAsset.assetFolder}. Download will be attempted if current etag differs.`
			);
		} else {
			// Force Chromium to go get a fresh download from the server (don't use an asset from the browser cache)
			headers["cache-control"] = "no-cache";
			logger.info(
				`No existing asset etag found. Downloading asset "${downloadAsset.alias}" from ${downloadAsset.url} to ${downloadAsset.assetFolder}.`
			);
		}

		const { err, data } = await stream(downloadAsset.url, { returnStream: true, logger, headers });
		if (err || !data || !data.data) {
			// Remove the folder so that a download will be attempted the next time
			fse.removeSync(downloadAsset.assetFolder);

			const error = `Error downloading "${downloadAsset.alias}" from ${downloadAsset.url} into ${downloadAsset.assetFolder}. Original Error: ${err}`;
			return { err: error };
		}

		const { statusCode } = data.response;
		const newEtag = objectGetValueCaseInsensitive(data.response.headers, "etag") as string;

		// If we didn't get an etag, write an error about caching
		if (statusCode == 200 && !newEtag) {
			logger.error(
				`Asset "${downloadAsset.alias}" retrieved without etag.  Caching will be disabled for this asset, which will affect performance.  Please host AppAssets on a modern webserver`
			);
		} else {
			logger.info(
				`Asset "${downloadAsset.alias}" successfully retrieved with statusCode:${statusCode} and Etag: ${newEtag}`
			);
		}

		// 304 Not Modified implies we should use cached version
		if (data.response.statusCode !== 304) {
			// Save the new etag and delete the existing directory
			fs.writeFileSync(etagFilePath, JSON.stringify({ etag: newEtag, etagUrl: asset.src, version: asset.version }));

			try {
				await retry(() => {
					// remove existing asset folder, this is a noop if the folder doesn't exist
					fse.removeSync(downloadAsset.assetFolder);

					// Verify that the folder was deleted
					if (fse.existsSync(downloadAsset.assetFolder)) {
						try {
							// Check whether the user has read/write/execute access on the folder to see if they have
							// permission to delete the folder
							fse.accessSync(
								downloadAsset.assetFolder,
								fs.constants.F_OK | fs.constants.R_OK | fs.constants.W_OK | fs.constants.X_OK
							);
							throw `${downloadAsset.assetFolder} was not deleted, but user has read/write/execute access`;
						} catch (e) {
							// There was an access error (e.g. user did not have permission to read/write/execute)
							throw `${downloadAsset.assetFolder} was not deleted. Access error: \n${JSON.stringify(e, null, "\t")}`;
						}
					}
				});
			} catch (e) {
				return { err: `Could not delete existing asset folder, so the new asset could not be downloaded:\n${e}` };
			}

			const { err: err2 } = await downloadAndUnzip(data.data, downloadAsset);
			if (err2) {
				// Remove the folder so that a download will be attempted the next time
				fse.removeSync(downloadAsset.assetFolder);

				const error = `Error unpacking the file downloaded for "${downloadAsset.alias}" from ${downloadAsset.url}. This can be caused by misconfiguration, file corruption at source or file corruption in transit. Make sure the url is configured properly in your manifest file and that the file is readable.
				Finsemble will attempt to download the file again when the application restarts.
				Original Error: ${err2}`;

				return { err: error };
			} else {
				logger.info(`Asset "${downloadAsset.alias}" Successfully unzipped to ${downloadAsset.assetFolder}`);
			}
		} else {
			logger.info(`Using existing asset "${downloadAsset.alias}" with etag ${newEtag}`);
		}

		return { data: downloadAsset };
	}

	/**
	 * Deletes all assets. Returns an error message or null if successful.
	 * @param alias - If set then delete that specific asset, otherwise delete them all
	 */
	deleteAssets(alias?: string | null): string | null {
		try {
			const { err, data: joinedPath } = pathJoinSecureRoot(this.assetsFolder, alias ?? "");
			if (err || !joinedPath) return err ?? `deleteAssets(): Assertion error: no joinedPath`;
			const folder = alias ? joinedPath : this.assetsFolder;
			fse.removeSync(folder);
		} catch (e: any) {
			return e.toString();
		}
		return null;
	}

	/**
	 * Deletes old versioned folders for an asset, keeping only the `maxVersions` most recently downloaded ones.
	 * `currentVersion` is always kept.
	 */
	private deleteOldVersionedAssets(alias: string, maxVersions: number, currentVersion: string): void {
		if (!fs.existsSync(this.assetsFolder)) return;
		const effectiveMax = Math.max(1, maxVersions);
		const folders: { folderPath: string; mtime: number }[] = [];

		for (const entry of fs.readdirSync(this.assetsFolder)) {
			if (!entry.startsWith(`${alias}-`) || entry === `${alias}-${currentVersion}`) continue;
			const { err, data: folderPath } = pathJoinSecureRoot(this.assetsFolder, entry);
			if (err || !folderPath) continue;
			const versionFilePath = path.join(folderPath, `${alias}.version.json`);

			try {
				const { mtimeMs } = fs.statSync(versionFilePath);
				folders.push({ folderPath, mtime: mtimeMs });
			} catch {
				// No version file — skip this folder
			}
		}

		// Keep effectiveMax - 1 older versions (current counts as one of the allowed slots)
		folders.sort((a, b) => b.mtime - a.mtime);
		for (const { folderPath } of folders.slice(effectiveMax - 1)) {
			try {
				fse.removeSync(folderPath);
				logger.log(`Removed old asset folder: ${folderPath}`);
			} catch (e) {
				logger.error(`Failed to remove old asset folder ${folderPath}: ${e}`);
			}
		}
	}

	/**
	 * Download all assets specified in the `appAssets` attribute of the manifest.
	 * @param appAssets - Array of asset configs (e.g. from `manifest.appAssets`)
	 * @returns Promise that resolves to an error string if there was an error
	 */
	downloadAssets(appAssets: Asset[]): StandardPromise<DownloadedAsset[]> {
		return new Promise(async (resolve) => {
			logger.info("ExternalApplicationManager.downloadAssets()");
			if (!appAssets || appAssets.length === 0) {
				logger.log("No appAssets to load");
				resolve({ data: [] });
				return;
			}
			if (!Array.isArray(appAssets)) {
				const err = `Manifest appAssets entry is not an array: "${typeof appAssets}"`;
				logger.error(err);
				resolve({ err });
				return;
			}

			// Download all assets in parallel. Chromium may throttle the number of simultaneous downloads.
			const promises = appAssets.map((asset) => this.maybeDownloadAsset(asset));
			const resultStatuses = await Promise.allSettled(promises);

			// If any download fails then return an error
			const maybeErrors = resultStatuses
				.map((result) => {
					const promiseRejectError = result.status === "rejected" ? result.reason : null;
					const returnedError = result.status === "fulfilled" ? result.value.err : null;
					const error = returnedError ?? promiseRejectError;
					if (error) logger.error(`downloadAssets(): ${error}`);
					return error;
				})
				.filter((e) => e);

			if (maybeErrors.length) {
				resolve({ err: `All app assets didn't download. Details: ${maybeErrors.join(",")} ` });
			} else {
				// typescript hack to force not nullish
				const assets = resultStatuses
					.filter(isFulfilled)
					.map((p) => p.value.data)
					.filter((p) => p) as DownloadedAsset[];
				resolve({ data: assets });
			}
		});
	}

	/**
	 * Returns a path to an asset that was previously downloaded based on
	 * an appAsset entry.
	 *
	 * @param alias An alias that should map into app assets
	 * @returns {err, data} Where data is a path to the executable
	 */
	maybePathFromAlias(alias?: string): StandardResponse<string> {
		if (!alias) return {};
		const asset = this.assets[alias];
		if (!asset) {
			return { err: `Asset not found. ${alias} -> ${this.assets}` };
		}
		return pathJoinSecureRoot(asset.assetFolder, asset.executable);
	}

	/**
	 * Spawns an application outside of Electron.
	 *
	 * @param alias - If set, alias must match a previously downloaded app asset
	 * @param path - Optional direct path to an executable to launch
	 * @param certificate - If certificate.trusted is true then Electron's signtool will be used to verify the app
	 * @param options - Options compatible with Node.js child_process.spawn()
	 * @param arguments - An array of arguments to be passed to the application (See Node.js child_process.spawn)
	 * @param env - A map of environment variables to set for the application (See Node.js child_process.spawn)
	 */
	async spawn({
		alias,
		path: filePath,
		certificate,
		options = {},
		arguments: spawnArguments,
		env,
	}: {
		alias?: string;
		path?: string;
		certificate?: {
			trusted: boolean;
		};
		options?: SpawnOptionsWithoutStdio;
		arguments?: string[] | string;
		env?: Record<string, string>;
	}): StandardPromise<void> {
		const { err: aliasErr, data: aliasPath } = this.maybePathFromAlias(alias);
		if (aliasErr) {
			return { err: aliasErr };
		}

		const spawnPath = filePath ?? aliasPath;

		if (!spawnPath) {
			return { err: "No path provided for external application. Please provide a valid path." };
		}

		logger.info(`ExternalApplicationManager.spawn ${spawnPath}`);

		// trusted indicates we should verify the executable's signature
		// imesner: 20220601: We are deprecating verifying with signtool to plug a security hole
		//
		// We could use the private version included within "electron-winstaller"
		// This is a pretty bad hack but should suffice until we verify that nobody is using this functionality
		// and remove it or find an alternative (like enforcing that signtool must be on the $PATH).
		// The hack is made worse by using eval.  Webpack replaces require.resolve with the module ID or name,
		// even for those modules declared as external.  I've tried the following to circumvent this without luck:
		// - webpack magic comments: require.resolve(/* webpackIgnore: true */ "electron-winstaller");
		// - require.resolve.paths (tries to call paths() on webpack's internal function)
		// - wrap in a commonjs module and use a webpack magic comment: require(/* webpackIgnore: true */ "./winstallerPath");
		// - webpack ignorePlugin (I may have not set it up correctly)
		// Only eval worked.  Since this is deprecated and we will replace it someday (all to avoid the npm signtool package)
		if (certificate?.trusted) {
			const err = "Trusted app verification has been removed!";
			logger.error(err);
			return { err };
		}

		// It is possible to verify certificate.serial and certificate.thumbprint using c++ or c#
		// https://support.microsoft.com/en-us/help/323809/how-to-get-information-from-authenticode-signed-executables
		// https://docs.microsoft.com/en-us/dotnet/api/system.security.cryptography.x509certificates.x509certificate2?view=netframework-4.7.2

		const argumentsArray = argumentsToArray(spawnPath, spawnArguments);

		logger.info(
			`Attempting to start external application ${spawnPath} with arguments ${spawnArguments} with options ${options}`
		);

		let ammendedOptions = {
			...options,
			// Change the current working directory to the spawn path if not provided
			cwd: path.dirname(spawnPath),
		};

		// Set env directly if not already provided in options
		if (!ammendedOptions.env && env) ammendedOptions.env = env;

		const { err, data: externalApp } = spawnApp({
			filePath: spawnPath,
			arguments: argumentsArray,
			options: ammendedOptions,
		});

		if (externalApp) this.externalApps.push(externalApp);

		return { err };
	}

	/**
	 * Waits for all external apps to exit of their own accord (callers are expected to have already
	 * triggered a graceful shutdown, e.g. via the assimilation WebSocket shutdown message), then
	 * force-kills with SIGKILL any process that has not exited within the grace period.
	 *
	 * @param gracePeriodMs How long to wait before force-killing a process that has not exited.
	 */
	async closeAllAppsAndWait(gracePeriodMs = 3000): Promise<void> {
		await Promise.all(
			this.externalApps.map((app) => {
				let timeoutId: NodeJS.Timeout;
				const forceKillAfterTimeout = new Promise<void>((resolve) => {
					timeoutId = setTimeout(async () => {
						logger.warn(
							`External app ${app.filePath} did not exit within ${gracePeriodMs}ms grace period — force-killing`
						);
						try {
							app.kill("SIGKILL");
						} catch (err) {
							logger.warn(`Failed to kill external application ${app.filePath}: ${err}`);
						}

						// Secondary timeout: if the process never emits "close" (e.g. kill failed or spawn
						// error prevented the "close" event), resolve anyway so shutdown is not blocked.
						let secondaryTimeoutId: ReturnType<typeof setTimeout>;
						const secondaryTimeout = new Promise<void>((resolveSecondary) => {
							secondaryTimeoutId = setTimeout(() => {
								logger.warn(
									`External app ${app.filePath} did not emit close after force-kill — proceeding with shutdown`
								);
								resolveSecondary();
							}, gracePeriodMs);
						});
						await Promise.race([app.waitForClose().then(() => clearTimeout(secondaryTimeoutId)), secondaryTimeout]);
						resolve();
					}, gracePeriodMs);
				});
				return Promise.race([app.waitForClose().then(() => clearTimeout(timeoutId)), forceKillAfterTimeout]);
			})
		);
	}
}
